import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Evoryn } from '../../Servcie/evoryn';
import { ToastrNotificationService } from '../../Common/toastr-notification.service';
import { SearchService } from '../../Service/search';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { BankStatus, WithdrawStatus } from '../../Common/enum';

@Component({
  selector: 'app-bank-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [DatePipe],
  templateUrl: './bank-detail.html',
  styleUrls: ['./bank-detail.css'],
})
export class BankDetail implements OnInit, OnDestroy {
  isModalOpen = false;
  isWithdrawModalOpen = false;

  bankForm!: FormGroup;
  withdrawForm!: FormGroup;

  allBankDetails: any[] = [];
  allWithdrawals: any[] = [];

  filteredBankDetails: any[] = [];
  filteredWithdrawals: any[] = [];

  currentSearchText: string = '';
  private searchSubscription!: Subscription;

  constructor(
    private api: Evoryn,
    private toastr: ToastrNotificationService,
    private datePipe: DatePipe,
    private searchService: SearchService,
    private fb: FormBuilder
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.searchSubscription = this.searchService.currentSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(text => {
        this.currentSearchText = text;
        this.applyFilter();
      });

    this.fetchBankData();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  initForm() {
    this.bankForm = this.fb.group({
      bankName: ['', [Validators.required, Validators.pattern('^[a-zA-Z ]+$')]],
      accountNumber: ['', [Validators.required, Validators.pattern('^[0-9]{9,18}$')]],
      accountHolderName: ['', [Validators.required, Validators.pattern('^[a-zA-Z ]+$')]],
      ifscCode: ['', [Validators.required]]
    });

    this.withdrawForm = this.fb.group({
      bankID: ['', Validators.required],
      withdrawAmount: ['', [Validators.required, Validators.min(1), Validators.max(999999)]]
    });
  }

  isFieldInvalid(form: FormGroup, field: string): boolean {
    const control = form.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  fetchBankData() {
    const sessionUserId = Number(sessionStorage.getItem('userID')) || 0;
    if (sessionUserId === 0) return;

    this.api.fetchbankdetails(sessionUserId).subscribe({
      next: (res: any) => {
        if (res.status && res.data) {

          this.allBankDetails = (res.data.bank || []).map((b: any, index: number) => ({
            srNo: (index + 1).toString().padStart(2, '0'),
            bankID: b.bankID,
            bankName: b.bankName,
            accountNumber: b.accountNumber,
            accountHolderName: b.accountHolderName,
            ifscCode: b.ifscCode,
            status: this.getBankStatusText(b.bankStatus)
          }));

          this.allWithdrawals = (res.data.withdraws || []).map((w: any, index: number) => ({
            srNo: (index + 1).toString().padStart(2, '0'),
            amount: w.withdrawAmount,
            requestDate: this.datePipe.transform(w.requestedOn, 'dd MMM yyyy') || 'N/A',
            withdrawalDate: w.paidOnDate && w.paidOnDate !== '0001-01-01T00:00:00'
              ? this.datePipe.transform(w.paidOnDate, 'dd MMM yyyy')
              : 'Pending',
            status: this.getWithdrawStatusText(w.withdrawStatus)
          }));
        } else {
          this.allBankDetails = [];
          this.allWithdrawals = [];
        }
        this.applyFilter();
      },
      error: (err) => {
        this.toastr.showError('Failed to fetch bank data.');
        console.error(err);
      }
    });
  }

  getBankStatusText(status: number): string {
    switch (status) {
      case BankStatus.Approve: return 'Approved';
      case BankStatus.Reject: return 'Rejected';
      case BankStatus.Pending:
      default: return 'Pending';
    }
  }

  getWithdrawStatusText(status: number): string {
    switch (status) {
      case WithdrawStatus.Paid: return 'Delivered';
      case WithdrawStatus.Rejected: return 'Rejected';
      case WithdrawStatus.Requsted:
      default: return 'Requested';
    }
  }

  get approvedBanks() {
    return this.allBankDetails.filter(b => b.status === 'Approved');
  }

  applyFilter() {
    if (!this.currentSearchText || this.currentSearchText.trim() === '') {
      this.filteredBankDetails = [...this.allBankDetails];
      this.filteredWithdrawals = [...this.allWithdrawals];
    } else {
      const searchStr = this.currentSearchText.toLowerCase().trim();

      this.filteredBankDetails = this.allBankDetails.filter(b =>
        (b.bankName && b.bankName.toLowerCase().includes(searchStr)) ||
        (b.accountNumber && b.accountNumber.toLowerCase().includes(searchStr)) ||
        (b.accountHolderName && b.accountHolderName.toLowerCase().includes(searchStr))
      );

      this.filteredWithdrawals = this.allWithdrawals.filter(w =>
        (w.requestDate && w.requestDate.toLowerCase().includes(searchStr)) ||
        (w.status && w.status.toLowerCase().includes(searchStr))
      );
    }
  }

  @HostListener('window:openBankModal')
  onHeaderAddClick() {
    this.openModal();
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
    this.bankForm.reset();
  }

  saveBank() {
    if (this.bankForm.invalid) {
      this.bankForm.markAllAsTouched();
      Swal.fire('Invalid Details', 'Please check the highlighted fields.', 'warning');
      return;
    }

    const sessionUserId = Number(sessionStorage.getItem('userID')) || 0;
    const formVals = this.bankForm.value;

    const payload = {
      bankID: 0,
      userID: sessionUserId,
      bankName: formVals.bankName,
      accountNumber: formVals.accountNumber,
      accountHolderName: formVals.accountHolderName,
      ifscCode: formVals.ifscCode.toUpperCase()
    };

    this.api.addbank(payload).subscribe({
      next: (res: any) => {
        if (res.status) {
          Swal.fire('Success', res.message || 'Bank added successfully!', 'success');
          this.fetchBankData();
          this.closeModal();
        } else {
          this.toastr.showError(res.message || 'Failed to add bank.');
        }
      },
      error: (err) => {
        this.toastr.showError('API Error: Could not add bank.');
        console.error(err);
      }
    });
  }

  openWithdrawModal() {
    if (this.approvedBanks.length === 0) {
      Swal.fire('No Approved Banks', 'You must have at least one approved bank account to request a withdrawal.', 'warning');
      return;
    }
    this.isWithdrawModalOpen = true;
  }

  closeWithdrawModal() {
    this.isWithdrawModalOpen = false;
    this.withdrawForm.reset();
  }

  submitWithdrawal() {
    if (this.withdrawForm.invalid) {
      this.withdrawForm.markAllAsTouched();
      return;
    }

    const sessionUserId = Number(sessionStorage.getItem('userID')) || 0;
    const formVals = this.withdrawForm.value;

    const payload = {
      withdrawID: 0,
      bankID: Number(formVals.bankID),
      partnerUserID: sessionUserId,
      withdrawAmount: Number(formVals.withdrawAmount)
    };

    this.api.addwithdrawrequest(payload).subscribe({
      next: (res: any) => {
        if (res.status) {
          Swal.fire('Success', res.message || 'Withdrawal request submitted!', 'success');
          this.fetchBankData();
          this.closeWithdrawModal();
        } else {
          this.toastr.showError(res.message || 'Failed to request withdrawal.');
        }
      },
      error: (err) => {
        this.toastr.showError('API Error: Could not process request.');
        console.error(err);
      }
    });
  }
}